import {
  EllipsisOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Spin, Tooltip, Typography } from 'antd';
import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ConfirmDeletingElement } from '@app/components';
import {
  LEADS_RESOURCE,
  PREFIX,
  SALES_FUNNELS_PATH,
  SUCCESS,
} from '@app/constants';
import { handleCatchError, useDeleteSalesFunnelMutation } from '@app/services';

import { DELETE_SALES_FUNNEL_CONTENT } from '@pages/SalesFunnels/SalesFunnelsRegistry/constants/editSalesFunnelModal';

import { useViewSalesFunnelPageHeaderEffects } from '@pages/SalesFunnels/ViewSalesFunnel/hooks/useViewSalesFunnelPageHeaderEffects';

import { getHeaderDropdownItems } from '@pages/SalesFunnels/ViewSalesFunnel/data/headerDropdownItems';

import { WriteFirstModal } from './WriteFirstModal';

import '@pages/SalesFunnels/ViewSalesFunnel/styles/ViewSalesFunnelPageHeader.tablet.scss';

const { Title } = Typography;

// The "Добавить лид" modal on this route never had a populated registry list
// (the old provider cleared `leads` when leaving `/leads`), so an empty list is
// behavior-preserving. This also removes the page's dependency on the old
// `LeadsProvider`.
const NO_LEADS: never[] = [];

export const ViewSalesFunnelPageHeader: React.FC = () => {
  const navigate = useNavigate();
  const [deleteItem] = useDeleteSalesFunnelMutation();

  const {
    isWriteFirstModalOpen,
    setIsWriteFirstModalOpen,
    userSocialIntegrations,
    isDeletingModalOpen,
    setIsDeletingModalOpen,
    showNotification,
    activeBranchId,
    currentSalesFunnel,
  } = useViewSalesFunnelPageHeaderEffects();

  const dropdownItems = useMemo(
    () => getHeaderDropdownItems(currentSalesFunnel, setIsDeletingModalOpen),
    [currentSalesFunnel, setIsDeletingModalOpen]
  );

  const handleDeleteSalesFunnel = async () => {
    if (currentSalesFunnel) {
      try {
        await deleteItem({
          id: currentSalesFunnel.Id,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        showNotification('Воронка успешно удалена', SUCCESS);

        navigate(`${SALES_FUNNELS_PATH}`);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    }
  };

  return (
    <div className={`${PREFIX}view-sales-page-header`}>
      <div className="left-block">
        {currentSalesFunnel ? (
          <Title level={4} className="header-title">
            {`${currentSalesFunnel.Name} - воронка продаж`}
          </Title>
        ) : (
          <Spin />
        )}
        <Tooltip title="Лиды">
          <Link to={LEADS_RESOURCE}>
            <Button icon={<UnorderedListOutlined className="leads-icon" />} />
          </Link>
        </Tooltip>
      </div>

      <div className="right-block">
        <Button
          icon={<PlusOutlined />}
          onClick={() => setIsWriteFirstModalOpen(true)}
          type="primary"
        >
          Добавить лид
        </Button>
        <Dropdown menu={{ items: dropdownItems }}>
          <Button icon={<EllipsisOutlined />} />
        </Dropdown>
      </div>

      <WriteFirstModal
        isWriteFirstModalOpen={isWriteFirstModalOpen}
        onCancel={() => {
          setIsWriteFirstModalOpen(false);
        }}
        userSocialIntegrations={userSocialIntegrations}
        leads={NO_LEADS}
      />

      <ConfirmDeletingElement
        modalOpen={isDeletingModalOpen}
        setModalOpen={setIsDeletingModalOpen}
        handleDelete={handleDeleteSalesFunnel}
        content={DELETE_SALES_FUNNEL_CONTENT}
        title="Удаление воронки продаж"
      />
    </div>
  );
};
